import React from "react";
import { Container, Row, Col, Card, Badge } from "react-bootstrap";
import { BsCalendar, BsFacebook, BsLinkedin, BsTwitter } from "react-icons/bs";
import "../assets/styles/NewsRoomDetailsData.css";

const blogDetails = {
  1: {
    title: "The Future of AI: Transforming Business Operations in 2025",
    category: "Artificial Intelligence",
    author: "Async Vibe Team",
    date: "March 15, 2025",
    readTime: "5 min read",
    image: '/assets/aiFeatured.jpg',
    tags: ['AI', 'Machine Learning', 'Business Automation'],
    content: [
      {
        heading: "Introduction to AI in Business",
        text: "Artificial Intelligence has evolved from a futuristic concept to a practical business tool that's reshaping industries across the globe. In 2025, AI is no longer optional—it's essential for staying competitive in the digital marketplace."
      },
      {
        heading: "Key AI Applications in Business",
        text: "From automated customer service chatbots to predictive analytics for inventory management, AI is streamlining operations and reducing costs. Companies implementing AI solutions report an average 30% increase in operational efficiency and a 25% reduction in operational costs."
      },
      {
        heading: "Machine Learning for Decision Making",
        text: "Machine learning algorithms are now capable of analyzing vast amounts of data to provide actionable insights. This enables businesses to make data-driven decisions faster than ever before, leading to improved customer experiences and better resource allocation."
      },
      {
        heading: "The Future of AI Integration",
        text: "As we move forward, AI will become even more integrated into business processes. Companies that embrace AI early will have a significant advantage over competitors who wait to adopt these technologies."
      }
    ]
  },
  2: {
    title: "Flutter vs React Native: Choosing the Right Cross-Platform Solution",
    category: "Mobile Development",
    author: "Async Vibe Team",
    date: "March 12, 2025",
    readTime: "7 min read",
    image: '/assets/flutter.png',
    tags: ['Flutter', 'React Native', 'Mobile Apps', 'Cross-Platform'],
    content: [
      {
        heading: "Understanding Cross-Platform Development",
        text: "Cross-platform development has become the preferred choice for businesses looking to reach both iOS and Android users without maintaining separate codebases. Flutter and React Native are the two leading frameworks in this space."
      },
      {
        heading: "Flutter: Google's UI Toolkit",
        text: "Flutter, developed by Google, uses the Dart programming language and provides a rich set of pre-built widgets. It offers excellent performance with its own rendering engine and hot reload functionality for faster development cycles."
      },
      {
        heading: "React Native: Facebook's Framework",
        text: "React Native, created by Facebook, uses JavaScript and React principles. It allows developers to write native code when needed and provides access to native APIs, making it suitable for complex applications."
      },
      {
        heading: "Performance Comparison",
        text: "Flutter generally offers better performance due to its direct compilation to native code, while React Native provides more flexibility with native module integration. The choice depends on your specific project requirements and team expertise."
      }
    ]
  },
  3: {
    title: "Full Stack Development: Building Scalable Web Applications",
    category: "Web Development",
    author: "Async Vibe Team",
    date: "March 10, 2025",
    readTime: "8 min read",
    image: '/assets/webLanding.png',
    tags: ['Full Stack', 'Web Development', 'Scalability', 'Modern Tech'],
    content: [
      {
        heading: "What is Full Stack Development?",
        text: "Full stack development encompasses both frontend and backend development, allowing developers to work on all aspects of web applications. This comprehensive approach is essential for building modern, scalable web solutions."
      },
      {
        heading: "Frontend Technologies",
        text: "Modern frontend development relies on frameworks like React, Vue.js, and Angular. These tools provide component-based architectures, state management solutions, and responsive design capabilities that enhance user experience."
      },
      {
        heading: "Backend Architecture",
        text: "Backend development involves creating robust APIs, database design, and server-side logic. Technologies like Node.js, Python (Django/Flask), and Java (Spring Boot) provide the foundation for scalable backend services."
      },
      {
        heading: "Scalability and Performance",
        text: "Building scalable applications requires careful consideration of database design, caching strategies, and load balancing. Full stack developers must understand both frontend and backend optimization techniques to deliver high-performance applications."
      }
    ]
  }
};

const BlogsDetailsData = ({ blogId }) => {
  const blog = blogDetails[blogId] || blogDetails[1]; // Default to first blog if no ID

  return (
    <Container className="newsroom-container">
      <Row>
        <Col md={8}>
          {/* Blog Header */}
          <div className="blog-details-header mb-4">
            <div className="blog-category-badge mb-3">
              <Badge bg="primary" className="category-badge">
                {blog.category}
              </Badge>
            </div>
            <h1 className="blog-details-title">
              {blog.title}
            </h1>
            <div className="blog-meta-info">
              <span className="meta-item">
                <BsCalendar /> {blog.date}
              </span>
              <span className="meta-item">
                • {blog.readTime}
              </span>
              <span className="meta-item">
                • By {blog.author}
              </span>
            </div>
          </div>

          {/* Blog Image */}
          <div className="blog-details-image mb-4">
            <img 
              src={blog.image} 
              alt={blog.title} 
              className="blog-main-image"
            />
          </div>

          {/* Blog Content */}
          <div className="blog-details-content">
            {blog.content.map((section, index) => (
              <div key={index} className="blog-content-section mb-4">
                <h3 className="blog-content-heading">
                  {section.heading}
                </h3>
                <p className="blog-content-text">
                  {section.text}
                </p>
              </div>
            ))}
          </div>

          {/* Blog Tags */}
          <div className="blog-details-tags mt-4">
            <h6>Tags:</h6>
            <div className="tags-container">
              {blog.tags.map((tag, index) => (
                <Badge 
                  key={index} 
                  bg="light" 
                  text="dark" 
                  className="blog-tag me-2"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </Col>

        <Col md={4}>
          {/* Quick Links */}
          <Card className="newsroom-quick-links">
            <Card.Body>
              <h5 className="news-data-h5">Quick Links</h5>
              <ul>
                <li>Case Studies</li>
                <li>Newsroom</li>
                <li>Blogs</li>
                <li>Whitepaper</li>
              </ul>
            </Card.Body>
          </Card>

          {/* Related Content */}
          <Card className="newsroom-related-content">
            <Card.Body>
              <h5 className="news-data-h5">You may like</h5>
              <p>Automation – What's the fuss?</p>
              <button 
                className="read-more" 
                onClick={() => window.open('/blogs', '_self')}
              >
                READ MORE →
              </button>
            </Card.Body>
          </Card>

          {/* Social Share */}
          <div className="newsroom-social-icons">
            <BsFacebook /> <BsTwitter /> <BsLinkedin />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default BlogsDetailsData;
